import { zodResolver } from '@hookform/resolvers/zod';
import { Auth, createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { Firestore, doc, setDoc } from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useState, useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

type RegisterFormData = z.infer<typeof registerSchema>;

interface UseRegisterFormProps {
  auth: Auth;
  db: Firestore;
  functions: Functions;
  getBypassFlag: () => Promise<boolean>;
  httpsCallable: typeof httpsCallable;
  createUserWithEmailAndPassword: typeof createUserWithEmailAndPassword;
  signInWithEmailAndPassword: typeof signInWithEmailAndPassword;
  setDoc: typeof setDoc;
  doc: typeof doc;
  navigate: (path: string) => void;
  useTurnstile: () => {
    ready: boolean;
    renderWidget: () => void;
    getTurnstileToken: (containerId: string) => Promise<string>;
  };
  REDIRECT_AFTER_LOGIN: string;
  registerSchema: typeof registerSchema;
}

export function useRegisterForm({
  auth,
  db,
  functions,
  getBypassFlag: _getBypassFlag,
  httpsCallable,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword: _signInWithEmailAndPassword,
  setDoc,
  doc,
  navigate: _navigate,
  useTurnstile,
  REDIRECT_AFTER_LOGIN: _REDIRECT_AFTER_LOGIN,
  registerSchema,
}: UseRegisterFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState('');
  const { ready: turnstileReady, renderWidget, getTurnstileToken: _getTurnstileToken } = useTurnstile();
  const turnstileContainerId = 'turnstile-widget';
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    // Always sign out any existing user before registration
    if (auth && auth.currentUser) {
      try {
        await auth.signOut();
        console.log('[RegisterForm] Signed out existing user before registration');
      } catch (signOutErr) {
        console.warn('[RegisterForm] Error signing out before registration:', signOutErr);
        setError('Error signing out previous user: ' + (signOutErr instanceof Error ? signOutErr.message : String(signOutErr)));
        return;
      }
    }
    console.log('[RegisterForm] Starting registration for:', data.email);
    setIsLoading(true);
    setProgressMessage('Validating registration…');
    setError('');
    try {
      // Step 1: Validate registration (if applicable)
      try {
        const validateRegistration = httpsCallable(functions, 'validateRegistrationFunction');
        const result = await validateRegistration({
          email: data.email,
          password: data.password,
          ipAddress: null,
          countryCode: 'GB',
          turnstileToken: null, // Add if you have it
        });
        console.log('[RegisterForm] Validation result:', result.data);
        type ValidationResult = { success: boolean; message?: string };
        const validationData = result.data as ValidationResult;
        if (!validationData.success) {
          console.error('[RegisterForm] Validation failed:', validationData);
          setError(validationData.message || 'Registration validation failed.');
          setIsLoading(false);
          setProgressMessage('');
          return;
        }
      } catch (err) {
        console.error('[RegisterForm] Validation error:', err);
        setError(err instanceof Error ? err.message : 'Registration validation failed.');
        setIsLoading(false);
        setProgressMessage('');
        return;
      }
      // Step 2: Create user account
      setProgressMessage('Creating your account…');
      console.log('[RegisterForm] Creating Firebase Auth user');
      let userCredential;
      try {
        userCredential = await createUserWithEmailAndPassword(auth, data.email, data.password);
        console.log('[RegisterForm] User created and logged in successfully:', userCredential.user.uid);
      } catch (err) {
        console.error('[RegisterForm] Error creating user:', err);
        setError('Error creating user: ' + (err instanceof Error ? err.message : String(err)));
        setIsLoading(false);
        setProgressMessage('');
        return;
      }
      // Step 3: Set user profile in Firestore
      setProgressMessage('Saving your profile…');
      try {
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          createdAt: new Date().toISOString(),
        }, { merge: true });
        console.log('[RegisterForm] User profile saved to Firestore');
      } catch (err) {
        console.error('[RegisterForm] Error saving user profile:', err);
        setError('Error saving user profile: ' + (err instanceof Error ? err.message : String(err)));
        setIsLoading(false);
        setProgressMessage('');
        return;
      }

      setProgressMessage('Registration complete! Redirecting…');
      return console.log('[RegisterForm] Registration complete, showing success screen.');
    } catch (err) {
      console.error('[RegisterForm] Unexpected error:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
      setProgressMessage('');
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    isLoading,
    progressMessage,
    error,
    turnstileReady,
    renderWidget,
    turnstileContainerId,
  };
}
